import { isDarkColor } from "common/utilities/colors/colors";
import type { SequenceFeature } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import type { HandleResidueHover } from "sequenceAnalyser/components/Sequences/SequenceTable/SequenceTable";
import type { ColumnsLetterOccurrences } from "sequenceAnalyser/utilities/colorCalculations/colorCalculations";
import type { SelectedColorScheme } from "sequenceAnalyser/utilities/colors";
import residueProcessor from "sequenceAnalyser/utilities/residueProcessor/residueProcessor";
import {
  headerPixelHeight,
  RESIDUE_PIXEL_HEIGHT,
  RESIDUE_PIXEL_WIDTH,
  sequencePixelHeight,
} from "sequenceAnalyser/utilities/screen/screen";
import type { FeatureDefinitions, SequenceAnalyserTableView } from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import {
  hasAnyFeatureDefinitionVisible,
  unAlignedResiduePosition,
} from "sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser";

interface SequenceTableRowResiduesChunkProps {
  start: number;
  stop: number;
  colors?: SelectedColorScheme;
  onResidueHover: HandleResidueHover;
  view: SequenceAnalyserTableView;
  sequenceResidues: string;
  sequenceId: number;
  sequenceName: string;
  columnsLetterOccurrences: ColumnsLetterOccurrences;
  sequenceQualifiedId: string;
  noFeaturesColors: boolean;
  features: SequenceFeature[];
  rowId: number;
  featureDefinitions: FeatureDefinitions;
  hasColorsAndFeaturesLayout: boolean;
  maskingResidue: string;
}

export const SequenceTableRowResiduesChunk = ({
  start,
  stop,
  sequenceResidues,
  sequenceId,
  sequenceName,
  sequenceQualifiedId,
  features,
  featureDefinitions,
  colors,
  onResidueHover,
  noFeaturesColors,
  hasColorsAndFeaturesLayout,
  columnsLetterOccurrences,
  view,
  rowId,
  maskingResidue,
}: SequenceTableRowResiduesChunkProps) => {
  const residues = sequenceResidues.slice(start, stop);
  const hasColors = colors !== undefined;
  const hasFeatures = hasAnyFeatureDefinitionVisible(featureDefinitions);

  return (
    <div
      // Width should always be set so the page will get its real width/scrollbar,
      // even though all residues are not rendered
      style={{
        display: "inline-block",
        width: `${residues.length * RESIDUE_PIXEL_WIDTH}px`,
      }}
      data-chunk-rendered={true}
    >
      {[...residues].map((residue, index) => {
        const { residueId, residueForView, position, visibleDefs, isFeatureColorDark, color, defs, colorOpacity } =
          residueProcessor({
            sequenceId,
            position: start + index,
            hasFeatures,
            features,
            noFeaturesColors,
            featureDefinitions,
            residue,
            view,
            rowId,
            columnsLetterOccurrences,
            colors,
            maskingResidue,
          });

        return (
          <div
            id={residueId}
            key={residueId}
            className={[
              "selectable",
              // If a color is not set, show features overlap strippes on the whole element
              !noFeaturesColors && !hasColors && visibleDefs.length > 1
                ? `${isFeatureColorDark ? "darkFeatureStrippes" : "lightFeatureStrippes"}`
                : "",
              !color || !isDarkColor(color) ? "lightBg" : "darkBg",
            ].join(" ")}
            data-sequence={sequenceId}
            data-position={position}
            data-row-id={rowId}
            onMouseEnter={(e) => {
              let offsetTop = headerPixelHeight();
              let offsetLeft = 0;

              const wrapperElement = document.getElementById("sequencesWrapper");
              if (wrapperElement) {
                const bounds = wrapperElement.getBoundingClientRect();
                offsetTop = bounds.top - RESIDUE_PIXEL_HEIGHT;
                offsetLeft = bounds.left + RESIDUE_PIXEL_WIDTH;
              }

              onResidueHover({
                letter: residue,
                sequenceQualifiedId,
                sequenceName,
                position,
                unAlignedPosition:
                  residue === "."
                    ? undefined
                    : unAlignedResiduePosition([{ id: 1, residues: sequenceResidues }], 1, position),
                top: e.pageY - offsetTop,
                left: e.pageX + offsetLeft,
                featureDefinitions: defs,
              });
            }}
            style={{
              backgroundColor: color ? `rgba(${color.join(",")}, ${colorOpacity})` : undefined,
              height: hasColorsAndFeaturesLayout ? "18px" : `${sequencePixelHeight(false, false)}px`,
              left: `${index * RESIDUE_PIXEL_WIDTH + start * RESIDUE_PIXEL_WIDTH}px`,
              display: "inline-block",
              top: 0,
              position: "absolute",
              marginBottom: hasColorsAndFeaturesLayout ? "5px" : undefined,
            }}
          >
           {residueForView}
          </div>
        );
      })}
    </div>
  );
};
